import React, { useEffect, useState } from 'react';

import { TextField, InputAdornment, IconButton, Grid, makeStyles, useMediaQuery, useTheme, Typography } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';

import ReportStatusCell from './ReportStatusCell';
import ShareLinkCell from './ShareLinkCell';
import VisitDateCell from './VisitDateCell';
import TableDense from '../common/TableDense';

const useStyles = makeStyles((theme) => ({
  responsiveFullWidth: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  searchContainer: {
    borderBottom: '1px solid #F5F5F5',
    paddingLeft: '8px',
  },
  searchTextContainer: {
    display: 'inline-block',
    width: '175px',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      paddingRight: 0,
      width: '100%',
    },
  },
  searchTextField: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  tableContainer: {
    paddingRight: '2.5rem',
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
    },
  },
  typographyMobileDetail: {
    color: '#999999',
    fontSize: '10px',
    fontWeight: '500',
    lineHeight: '12px',
  },
  uploadFileContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 15,
      width: '100%',
    },
  },
}));

const generateColumns = ({ onShareLink, onViewVideoReport }) => {
  return [
    {
      label: 'Description',
      name: 'description',
      render: (value, data) => {
        const description = data?.title || data?.case?.uploadSharedInfo?.studyName || data?.case?.caseLinkInfo?.studyDescription || value;

        return <span style={{ color: description ? '#172B4D' : '#999999', fontSize: '14px', lineHeight: '16px' }}>{description || 'N/A'}</span>;
      },
    },
    {
      label: 'Requested',
      name: 'createdAt',
      render: (value) => {
        return <VisitDateCell date={value} />;
      },
    },
    {
      label: 'Video Report',
      name: 'status',
      render: (value, data) => {
        const handleViewVideoReport = () => {
          if (onViewVideoReport) onViewVideoReport(data.id);
        };

        return ['in-progress', 'completed', 'not-paid'].includes(value) ? (
          <ReportStatusCell status={value} onClick={handleViewVideoReport} />
        ) : (
          <span
            style={{
              color: '#999999',
              fontSize: '14px',
              lineHeight: '16px',
            }}>
            N/A
          </span>
        );
      },
    },
    {
      label: 'Share Report',
      name: 'shareLink',
      render: (_value, data) => {
        const caseStatus = data?.case?.status?.value;
        const link = { id: data?.id };

        return ['completed'].includes(caseStatus) ? (
          <ShareLinkCell link={link} onShareVideoReport={onShareLink} />
        ) : (
          <span
            style={{
              color: '#999999',
              fontSize: '14px',
              lineHeight: '16px',
            }}>
            N/A
          </span>
        );
      },
    },
  ];
};

const generateMobileColumns = ({ onShareLink, onViewVideoReport, classes }) => {
  return [
    {
      label: 'Description',
      name: 'description',
      render: (value, data) => {
        const visitDateValue = data.createdAt;
        const descriptionValue = data?.title || data.description;
        const statusValue = data.status;

        const caseStatus = data?.case?.status?.value;
        const shareLinkValue = { id: data?.id };
        const isCompleted = ['completed'].includes(caseStatus);

        const handleViewVideoReport = () => {
          if (onViewVideoReport) onViewVideoReport(data.id);
        };

        const renderDescription = (
          <div>
            {descriptionValue ? (
              <span style={{ color: '#172B4D', fontSize: '14px', lineHeight: '16px' }}>{descriptionValue}</span>
            ) : (
              <span
                style={{
                  color: '#999999',
                  fontSize: '14px',
                  lineHeight: '16px',
                }}>
                N/A
              </span>
            )}
          </div>
        );

        const renderVisitDate = (
          <div>
            <Typography>
              <span className={classes.typographyMobileDetail}>Requested:</span> <VisitDateCell date={visitDateValue} />
            </Typography>
          </div>
        );

        const renderVideoReportStatus = (
          <div>
            <Typography>
              <span className={classes.typographyMobileDetail}>Video Report:</span>{' '}
              {['in-progress', 'completed', 'not-paid'].includes(statusValue) ? (
                <ReportStatusCell status={statusValue} onClick={handleViewVideoReport} />
              ) : (
                <span
                  style={{
                    color: '#999999',
                    fontSize: '14px',
                    lineHeight: '16px',
                  }}>
                  N/A
                </span>
              )}
            </Typography>
          </div>
        );

        const renderShareReport = (
          <div style={{ marginTop: '4px' }}>
            <Typography style={{ alignItems: 'center', display: 'flex' }}>
              <span className={classes.typographyMobileDetail} style={{ marginRight: '4px' }}>
                Share Report:
              </span>{' '}
              {isCompleted ? (
                <ShareLinkCell link={shareLinkValue} onShareVideoReport={onShareLink} />
              ) : (
                <span
                  style={{
                    color: '#999999',
                    fontSize: '14px',
                    lineHeight: '16px',
                  }}>
                  N/A
                </span>
              )}
            </Typography>
          </div>
        );

        return (
          <div>
            {renderDescription}
            {renderVisitDate}
            {renderVideoReportStatus}
            {renderShareReport}
          </div>
        );
      },
    },
  ];
};

export default ({ total, studies, selectedRecord, onCriteriaChange, onStudySelected, onShareLink, onViewVideoReport, searchCriteria }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [keyword, setKeyword] = useState('');
  const [columns, setColumns] = useState([]);
  const classes = useStyles();

  const onSearchChange = (event) => {
    setKeyword(event.target.value);
  };

  const defaultOptions = {
    checkIsRowSelected: (selectedRow, data) => {
      if (!selectedRow?.length) return false;
      return selectedRow.includes(data.id);
    },
    onRowSelectionChange: (currentRowsSelected) => {
      const id = currentRowsSelected?.itemData?.id;
      setSelectedRow([id]);
      onStudySelected(id);
    },
    onTableChange: (action, tableState) => {
      const criteria = {};
      switch (action) {
        case 'changePage':
          criteria.page = tableState.page;
          break;
        case 'changeRowsPerPage':
          criteria.limit = tableState.rowsPerPage;
          tableOptions.rowsPerPage = tableState.rowsPerPage;
          break;
        default:
          return;
      }
      onCriteriaChange(criteria);
    },
    rowsSelected: [],
    serverSide: true,
  };

  const [selectedRow, setSelectedRow] = useState([]);
  const [tableOptions, setTableOptions] = useState(defaultOptions);
  const [patientStudies, setPatientStudies] = useState(studies);

  useEffect(() => {
    if (isDesktop) setColumns(generateColumns({ classes, onShareLink, onViewVideoReport }));
    else setColumns(generateMobileColumns({ classes, onShareLink, onViewVideoReport }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onShareLink, isDesktop]);

  useEffect(() => {
    setTableOptions({ ...tableOptions, rowsSelected: selectedRow });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow]);

  useEffect(() => {
    setPatientStudies(studies);
  }, [studies]);

  useEffect(() => {
    if (!selectedRecord || !studies) return;

    const study = studies.filter((e) => e.id === selectedRecord.id);
    if (study?.length) {
      setTableOptions({ ...tableOptions, rowsSelected: [study[0].id] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecord, studies]);

  useEffect(() => {
    setTableOptions({
      ...tableOptions,
      count: total,
      limit: searchCriteria?.limit || 10,
      page: searchCriteria?.page || 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, searchCriteria]);

  const onSearchClick = () => {
    onCriteriaChange({ keyword });
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <TableDense tableId="dashboard-studies" hideHeader={!isDesktop} options={tableOptions} columns={columns} data={patientStudies || []}>
          <div className={`text-left ${classes.searchContainer}`}>
            <TextField
              className={classes.searchTextField}
              onChange={onSearchChange}
              onKeyUp={(evt) => {
                if (evt.key === 'Enter') {
                  onSearchClick();
                }
              }}
              placeholder="Search"
              InputProps={{
                startAdornment: (
                  <InputAdornment>
                    <IconButton edge="start" onClick={onSearchClick}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              type="text"
            />
          </div>
        </TableDense>
      </Grid>
    </Grid>
  );
};
